import { useEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';

import { getDocument } from '@/lib/utils';
import type { SharedData } from '@/types/shared';

const GOOGLE_TAG_SCRIPT_ID = 'google-analytics-tag';

type AnalyticsWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
};

export function GoogleAnalyticsTracking() {
  const { site, previewMode } = usePage<SharedData>().props;
  const measurementId = site?.googleAnalyticsMeasurementId;
  const initializedMeasurementId = useRef<string | null>(null);

  useEffect(() => {
    if (!measurementId || previewMode) return;
    if (initializedMeasurementId.current === measurementId) return;

    const doc = getDocument();
    const analyticsWindow = doc?.defaultView as AnalyticsWindow | null;
    if (!doc || !analyticsWindow) return;

    analyticsWindow.dataLayer ||= [];
    analyticsWindow.gtag ||= function googleTag() {
      // biome-ignore lint/complexity/noArguments: Google's command queue expects native Arguments entries.
      analyticsWindow.dataLayer?.push(arguments);
    };

    if (!doc.getElementById(GOOGLE_TAG_SCRIPT_ID)) {
      const script = doc.createElement('script');
      script.id = GOOGLE_TAG_SCRIPT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      doc.head.append(script);
      analyticsWindow.gtag('js', new Date());
    }

    // GA4 Enhanced Measurement tracks Inertia history changes automatically.
    analyticsWindow.gtag('config', measurementId);
    initializedMeasurementId.current = measurementId;
  }, [measurementId, previewMode]);

  return null;
}
